/**
 * Draft <-> stored translation for the attribute Validation editor.
 *
 * Admins pick plain-English rule kinds; the backend stores the rule shapes it
 * already enforces (`common/dynamic_attributes/validation.py`). Regex is a
 * deliberate omission: a pattern nobody on the tenant's side can read is how a
 * stale one goes unnoticed, so it stays authorable through the API only.
 *
 * Kinds map 1:1 onto stored rules - no folding - so nothing is lost on a
 * round trip. Rules this editor cannot model are carried through untouched by
 * `draftsFromRules` -> `unmanaged` -> `rulesFromDrafts`, so an engineer's regex
 * survives an admin renaming the field.
 */

import type { ValidationRule } from "@/shared/lib/forms";

export type SerialRangeBound = { from: string; to: string };

export type ValidationDraft =
  | { kind: "serial_range"; ranges: SerialRangeBound[]; message: string }
  | { kind: "starts_with"; value: string; message: string }
  | { kind: "ends_with"; value: string; message: string }
  | { kind: "min_length"; length: string; message: string }
  | { kind: "max_length"; length: string; message: string };

export type ValidationDraftKind = ValidationDraft["kind"];

/**
 * Rule kinds this editor offers, per backend `FIELD_TYPE_VALIDATION_RULES`.
 * Translation key + English default; the editor resolves them through `t()`.
 */
export const DRAFT_KIND_LABELS: Record<
  ValidationDraftKind,
  { key: string; label: string }
> = {
  serial_range: {
    key: "templates.validation.kind.serialRange",
    label: "Code ranges",
  },
  starts_with: {
    key: "templates.validation.kind.startsWith",
    label: "Starts with",
  },
  ends_with: { key: "templates.validation.kind.endsWith", label: "Ends with" },
  min_length: {
    key: "templates.validation.kind.minLength",
    label: "Minimum length",
  },
  max_length: {
    key: "templates.validation.kind.maxLength",
    label: "Maximum length",
  },
};

export const TEXT_DRAFT_KINDS: ValidationDraftKind[] = [
  "serial_range",
  "starts_with",
  "ends_with",
  "min_length",
  "max_length",
];

export const MULTI_SELECT_DRAFT_KINDS: ValidationDraftKind[] = [
  "serial_range",
];

export function draftKindsFor(fieldType: string): ValidationDraftKind[] {
  if (fieldType === "TEXT") return TEXT_DRAFT_KINDS;
  if (fieldType === "MULTI_SELECT") return MULTI_SELECT_DRAFT_KINDS;
  return [];
}

/** `*` and `?` are wildcards to the backend, so a literal one cannot be stored. */
export function stripWildcards(value: string): string {
  return value.replace(/[*?]/g, "");
}

export function emptyDraft(kind: ValidationDraftKind): ValidationDraft {
  switch (kind) {
    case "serial_range":
      return { kind, ranges: [{ from: "", to: "" }], message: "" };
    case "starts_with":
    case "ends_with":
      return { kind, value: "", message: "" };
    case "min_length":
    case "max_length":
      return { kind, length: "", message: "" };
  }
}

/** A blank `to` means the row pins one exact code. */
export function boundTo(bound: SerialRangeBound): string {
  return bound.to.trim() === "" ? bound.from.trim() : bound.to.trim();
}

/** Split a serial into its prefix and trailing digit run, mirroring the server. */
function splitSerial(value: string): [string, string] | null {
  const match = /^(.*?)(\d+)$/.exec(value.trim());
  return match ? [match[1], match[2]] : null;
}

/**
 * Why this row could never match anything, or null when it is fine.
 *
 * The same three checks the server runs when the rule is saved, so a bad row
 * is caught on the row that caused it instead of arriving as a toast naming
 * two values the admin then has to go and find.
 */
export type TranslateFn = (
  key: string,
  defaultValue: string,
  values?: Record<string, unknown>,
) => string;

export function rangeError(
  bound: SerialRangeBound,
  t?: TranslateFn,
): string | null {
  // Callers that only need "is this row usable?" omit `t` and get English;
  // the editor passes it so the admin reads the message in their language.
  const say = (
    key: string,
    english: string,
    values?: Record<string, unknown>,
  ) => (t ? t(key, english, values) : english);

  const from = bound.from.trim();
  const to = boundTo(bound);
  if (from === "") {
    return to === ""
      ? null
      : say(
          "templates.validation.error.enterFirstCode",
          "Enter the first code.",
        );
  }
  const low = splitSerial(from);
  const high = splitSerial(to);
  if (!low) {
    return say(
      "templates.validation.error.mustEndInDigitsFrom",
      "Must end in digits, e.g. ABC000100.",
    );
  }
  if (!high) {
    return say(
      "templates.validation.error.mustEndInDigitsTo",
      "Must end in digits, e.g. ABC000199.",
    );
  }
  if (low[0].toLowerCase() !== high[0].toLowerCase()) {
    return say(
      "templates.validation.error.sameLetters",
      `Both codes must start with the same letters (${low[0]} and ${high[0]}).`,
      { low: low[0], high: high[0] },
    );
  }
  if (low[1].length !== high[1].length) {
    return say(
      "templates.validation.error.sameDigits",
      `Both codes must have the same number of digits (${low[1].length} and ${high[1].length}).`,
      { low: low[1].length, high: high[1].length },
    );
  }
  if (low[1] > high[1]) {
    return say(
      "templates.validation.error.order",
      "The first code must come before the last.",
    );
  }
  return null;
}

const STARTS_WITH = /^([^*?]+)\*$/;
const ENDS_WITH = /^\*([^*?]+)$/;

/**
 * Split stored rules into editable drafts and everything else.
 *
 * The `unmanaged` half matters: dropping a rule the UI cannot render would let
 * saving an unrelated field silently delete a constraint.
 */
export function draftsFromRules(rules: ValidationRule[] | undefined): {
  drafts: ValidationDraft[];
  unmanaged: ValidationRule[];
} {
  const drafts: ValidationDraft[] = [];
  const unmanaged: ValidationRule[] = [];

  for (const rule of rules ?? []) {
    // Dropped from the product: leftover stored rows are not re-saved.
    if (
      (rule as { validation_type?: string }).validation_type ===
      "unique_across_variants"
    ) {
      continue;
    }
    const message = rule.failure_message ?? "";
    switch (rule.validation_type) {
      case "serial_range":
        drafts.push({
          kind: "serial_range",
          ranges: Array.isArray(rule.ranges)
            ? rule.ranges.map((r) => ({
                from: String(r?.from ?? ""),
                to: String(r?.to ?? ""),
              }))
            : [{ from: "", to: "" }],
          message,
        });
        break;
      case "min_length":
        drafts.push({
          kind: "min_length",
          length: String(rule.min_length ?? ""),
          message,
        });
        break;
      case "max_length":
        drafts.push({
          kind: "max_length",
          length: String(rule.max_length ?? ""),
          message,
        });
        break;
      case "pattern": {
        const starts = STARTS_WITH.exec(rule.pattern);
        const ends = ENDS_WITH.exec(rule.pattern);
        if (starts)
          drafts.push({ kind: "starts_with", value: starts[1], message });
        else if (ends)
          drafts.push({ kind: "ends_with", value: ends[1], message });
        else unmanaged.push(rule);
        break;
      }
      default:
        unmanaged.push(rule);
    }
  }

  return { drafts, unmanaged };
}

/** A draft the admin started but never filled in; storing it would reject everything. */
function isComplete(draft: ValidationDraft): boolean {
  switch (draft.kind) {
    case "serial_range":
      return draft.ranges.some(
        (r) => r.from.trim() !== "" && rangeError(r) === null,
      );
    case "starts_with":
    case "ends_with":
      return draft.value.trim() !== "";
    case "min_length":
    case "max_length":
      return (
        draft.length.trim() !== "" && Number.isFinite(Number(draft.length))
      );
  }
}

function draftToRule(draft: ValidationDraft): ValidationRule {
  const failure_message = draft.message.trim();
  switch (draft.kind) {
    case "serial_range":
      return {
        validation_type: "serial_range",
        ranges: draft.ranges
          .filter((r) => r.from.trim() !== "" && rangeError(r) === null)
          .map((r) => ({ from: r.from.trim(), to: boundTo(r) })),
        failure_message,
      };
    case "starts_with":
      return {
        validation_type: "pattern",
        pattern: `${stripWildcards(draft.value.trim())}*`,
        failure_message,
      };
    case "ends_with":
      return {
        validation_type: "pattern",
        pattern: `*${stripWildcards(draft.value.trim())}`,
        failure_message,
      };
    case "min_length":
      return {
        validation_type: "min_length",
        min_length: Number(draft.length),
        failure_message,
      };
    case "max_length":
      return {
        validation_type: "max_length",
        max_length: Number(draft.length),
        failure_message,
      };
  }
}

export function rulesFromDrafts(
  drafts: ValidationDraft[],
  unmanaged: ValidationRule[] = [],
): ValidationRule[] {
  return [...drafts.filter(isComplete).map(draftToRule), ...unmanaged];
}
